import { Texture } from 'three';

import { Cesium3dTilesPointCloudRenderer } from '../geodata/point-clouds/cesium-3d-tiles-point-cloud-renderer/cesium-3d-tiles-point-cloud-renderer';
import { Cesium3dTilesPointsMaterial } from '../geodata/point-clouds/cesium-3d-tiles-point-cloud-renderer/cesium-3d-tiles-points-material';
import { CesiumPointCloud } from '../geodata/point-clouds/cesium-point-cloud/cesium-point-cloud';
import { CesiumPointsMaterial } from '../geodata/point-clouds/cesium-point-cloud/points/material/cesium-point-material';
import { PointCloudHandle } from '../geodata/point-clouds/point-cloud-handle';
import { ProjectGeodataManager } from '../project-geodata-manager';
import { SkyviewScene } from '../scenes/skyview-scene';
import { SkyView } from '../skyview';
import { SunManager } from '../sun-manager';
import { ViewSettings } from '../view-settings';
import { OmittedLayer, PointCloudLayer, Position3, TiledModelLayer } from './manifest';

/** The number of point classes in the classification texture. */
const CLASS_COUNT = 67;
/** CesiumPointCloud draws its points at this times the tile's geometric error, see its factory. */
const REPLACE_POINT_SIZE_FACTOR = 1.5;

export const POINT_CLOUD_LAYER_ID = 'pointcloud';
export const TILED_MODEL_LAYER_ID = 'tiledModel';

export type TimelineLayers = {
  /** Painted as they're drawn in the viewer, under the assets. */
  layers: (TiledModelLayer | PointCloudLayer)[];
  /** A legacy scan's point cloud and tiled model, which the backend can't draw. */
  omitted: OmittedLayer[];
};

/** The pixels of the classification texture: a color per class, its alpha 0 for a hidden class. */
function readClassTexture(texture: Texture | undefined): Uint8ClampedArray | undefined {
  const image = texture?.image as CanvasImageSource & { width: number; height: number };
  if (!image?.width) {
    return undefined;
  }

  const canvas = document.createElement('canvas');
  canvas.width = image.width;
  canvas.height = image.height;
  const context = canvas.getContext('2d');
  if (!context) {
    return undefined;
  }

  context.drawImage(image, 0, 0);
  return context.getImageData(0, 0, image.width, 1).data;
}

/**
 * The colors and visibility of the point classes, as the classes panel shows them: its edited
 * pixels, or the texture's before it is opened.
 */
function getPointClasses(): PointCloudLayer['classes'] {
  const pixels =
    CesiumPointsMaterial.instance.imageData?.data ??
    readClassTexture(CesiumPointsMaterial.instance.material.uniforms.texture1.value as Texture);
  if (!pixels || pixels.length < CLASS_COUNT * 4) {
    console.warn('The point classes could not be read, the points keep their own colors.');
    return undefined;
  }

  return Array.from({ length: CLASS_COUNT }, (_, id) => ({
    id,
    color: `#${[0, 1, 2].map((i) => pixels[id * 4 + i].toString(16).padStart(2, '0')).join('')}`,
    visible: pixels[id * 4 + 3] !== 0,
  }));
}

/**
 * Describes the timeline's point cloud as the active renderer draws it: the point size, the classes
 * when colored by them, and the shading. Undefined for a renderer the backend can't follow.
 */
function getPointCloudLayer(pointCloudId: string, name: string): PointCloudLayer | undefined {
  const renderer = PointCloudHandle.instance.activePointCloud;
  const tiles = Cesium3dTilesPointsMaterial.instance;

  let pointSize: Omit<PointCloudLayer['pointSize'], 'mode' | 'scale'>;
  let displayClasses: boolean;
  if (renderer instanceof Cesium3dTilesPointCloudRenderer) {
    pointSize = {
      pointSizeScale: tiles.pointSizeScale,
      minPx: 1,
      maxPx: tiles.maximumAttenuation,
      leafSize: renderer._baseResolution,
    };
    displayClasses = tiles.displayClasses;
  } else if (renderer instanceof CesiumPointCloud) {
    pointSize = {
      pointSizeScale: 1 / REPLACE_POINT_SIZE_FACTOR,
      minPx: 0,
      maxPx: null,
      leafSize: null,
    };
    displayClasses = CesiumPointsMaterial.instance.displayClasses;
  } else {
    return undefined;
  }

  const { pointSizeInterpolationType, size } =
    PointCloudHandle.instance.pointSizeInterpolationSettings;
  const classes = displayClasses ? getPointClasses() : undefined;

  return {
    id: POINT_CLOUD_LAYER_ID,
    name: name || undefined,
    kind: 'pointcloud',
    pointCloudId,
    opacity: ViewSettings.instance.opacity.pointCloud,
    pointSize: {
      mode: pointSizeInterpolationType === 'Dynamic' ? 'dynamic' : 'fixed',
      scale: size,
      ...pointSize,
    },
    ...(classes ? { classes } : {}),
    ...(SunManager.instance.isDirectionEnabled()
      ? { shading: { lightDirection: tiles.shared.lightDirection.value.toArray() as Position3 } }
      : {}),
  };
}

/**
 * Describes the timeline's point cloud and tiled model shown in the flexible view, by their ids.
 * A legacy scan's are left out.
 */
export function collectTimelineLayers(skyview: SkyView): TimelineLayers {
  const result: TimelineLayers = { layers: [], omitted: [] };
  const scene = skyview.getActiveScene();
  if (!(scene instanceof SkyviewScene)) {
    return result;
  }

  const container = ProjectGeodataManager.instance().getActiveTimelineContainer();
  const name = container?.displayDate ?? '';

  // The tiled model is painted under the point cloud, whose points lie on its surface.
  if (scene.isTiledModelVisible()) {
    if (container?.tiledModelId) {
      result.layers.push({
        id: TILED_MODEL_LAYER_ID,
        name: name || undefined,
        kind: 'tiledModel',
        tiledModelId: container.tiledModelId,
        opacity: ViewSettings.instance.opacity.tiledModel,
      });
    } else {
      result.omitted.push({ kind: 'tiledModel', name, scanId: container?.scan });
    }
  }

  if (scene.isPointCloudVisible()) {
    const layer = container?.pointCloud
      ? getPointCloudLayer(container.pointCloud, name)
      : undefined;
    if (layer) {
      result.layers.push(layer);
    } else {
      result.omitted.push({ kind: 'pointcloud', name, scanId: container?.scan });
    }
  }

  return result;
}
